import { Button, Flex, Heading, Text } from '@conteg/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AllocationStatus, StorageUnitQuery } from 'types/generated/graphql';
import { appRoutes, substituteRouteParams } from 'utils/routing/routes';

export type ReservationAllocationsProps = {
  currentStorageUnitId: string;
  reservation:
    | StorageUnitQuery['storageUnit']['reservation']
    | null
    | undefined;
};

const ReservationAllocationsAndTrackingIds = ({
  currentStorageUnitId,
  reservation,
}: ReservationAllocationsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onGoToDetail = (targetStorageUnitId: string) =>
    navigate(
      substituteRouteParams(appRoutes.storageUnitDetail, {
        storageUnitId: targetStorageUnitId,
      })
    );

  const allocations =
    reservation?.allocations?.filter((allocation) =>
      [AllocationStatus.Started, AllocationStatus.Expired].includes(
        allocation.status
      )
    ) ?? [];

  // Collect all tracking identifiers from reservation.contents
  const allTrackingIdentifiers = (reservation?.contents ?? [])
    .flatMap((content) => content?.trackingIdentifiers ?? [])
    .map((ti: { identifier: string }) => ti?.identifier)
    .filter(Boolean) as string[];

  if (!reservation) {
    return null;
  }

  return (
    <Flex flexDirection="column" gap="3rem">
      {/* Section for lockers in this reservation */}
      {allocations.length > 0 && (
        <Flex flexDirection="column" gap="2rem">
          <Heading
            variant="h3"
            title={t('Page.StorageUnit.ReservationAllocations.Heading')}
            testId="reservation-allocations-heading"
          />
          {allocations.map((a) => {
            const isCurrent = a.storageUnitId === currentStorageUnitId;
            const storageUnitLabel = a.storageUnitName || a.storageUnitId;
            return (
              <Flex key={a.storageUnitId} gap="2rem" alignItems="center">
                <Text
                  content={`${t('Page.StorageUnit.ReservationAllocations.Locker')}: ${storageUnitLabel}${
                    isCurrent
                      ? ` ${t('Page.StorageUnit.ReservationAllocations.CurrentMarker')}`
                      : ''
                  }`}
                />
                {!isCurrent && (
                  <Button
                    title={t(
                      'Page.StorageUnit.ReservationAllocations.GoToDetail'
                    )}
                    variant="primary"
                    size="s"
                    onClick={() => onGoToDetail(a.storageUnitId)}
                  />
                )}
              </Flex>
            );
          })}
        </Flex>
      )}

      {/* Section for all tracking identifiers in this reservation */}
      {allTrackingIdentifiers.length > 0 && (
        <Flex flexDirection="column" gap="2rem">
          <Heading
            variant="h3"
            title={t('Page.StorageUnit.ReservationTrackingIdentifiers.Heading')}
            testId="reservation-tracking-identifiers-heading"
          />
          {allTrackingIdentifiers.map((identifier, index) => (
            <Text key={index} content={identifier} />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default ReservationAllocationsAndTrackingIds;
